import SignaturePad from 'signature_pad';

const normalizarDados = dados => {
  if (typeof dados === 'string') {
    return JSON.parse(dados);
  }
  return dados;
};

const obterLimites = (dados, margem = 5) => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  dados.forEach(stroke => {
    stroke.points.forEach(point => {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    });
  });
  return {
    minX: minX - margem,
    minY: minY - margem,
    maxX: maxX + margem,
    maxY: maxY + margem
  };
};

const criarCanvasVazio = ({ largura = 1, altura = 1 } = {}) => {
  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  return canvas;
};

const criarCanvasAssinatura = (
  dados,
  {
    corCaneta = '#000000',
    margem = 5,
    exportScale = 1,
    espessuraMultiplicador = 1,
    larguraMaxima = null,
    alturaMaxima = null
  } = {}
) => {
  dados = normalizarDados(dados);

  if (!Array.isArray(dados) || dados.length === 0) {
    return criarCanvasVazio();
  }

  const limites = obterLimites(dados, margem);

  const larguraOriginal = limites.maxX - limites.minX;
  const alturaOriginal = limites.maxY - limites.minY;

  let escala = 1;
  if (larguraMaxima || alturaMaxima) {
    const escalaLargura = larguraMaxima ? larguraMaxima / larguraOriginal : Infinity;
    const escalaAltura = alturaMaxima ? alturaMaxima / alturaOriginal : Infinity;
    escala = Math.min(escalaLargura, escalaAltura, 1);
  }
  const larguraFinal = Math.max(1, Math.round(larguraOriginal * escala));
  const alturaFinal = Math.max(1, Math.round(alturaOriginal * escala));

  const larguraOutput = Math.max(1, Math.round(larguraFinal * exportScale));
  const alturaOutput = Math.max(1, Math.round(alturaFinal * exportScale));

  const larguraRender = Math.max(1, Math.round(larguraOutput * espessuraMultiplicador));
  const alturaRender = Math.max(1, Math.round(alturaOutput * espessuraMultiplicador));

  const canvasRender = document.createElement('canvas');
  canvasRender.width = larguraRender;
  canvasRender.height = alturaRender;

  const padExportacao = new SignaturePad(canvasRender, {
    penColor: corCaneta
  });

  const escalaRenderX = larguraRender / larguraOriginal;
  const escalaRenderY = alturaRender / alturaOriginal;

  const dadosAjustados = dados.map(stroke => ({
    ...stroke,
    penColor: corCaneta,
    points: stroke.points.map(point => ({
      ...point,
      x: (point.x - limites.minX) * escalaRenderX,
      y: (point.y - limites.minY) * escalaRenderY
    }))
  }));

  padExportacao.fromData(dadosAjustados);
  padExportacao.off();

  const canvasFinal = document.createElement('canvas');

  canvasFinal.width = larguraOutput;
  canvasFinal.height = alturaOutput;

  const ctxFinal = canvasFinal.getContext('2d');

  ctxFinal.imageSmoothingEnabled = true;
  ctxFinal.imageSmoothingQuality = 'high';

  ctxFinal.drawImage(canvasRender, 0, 0, larguraRender, alturaRender, 0, 0, larguraOutput, alturaOutput);

  return canvasFinal;
};

const aplicarCorFundoCanvas = (canvasOrigem, corFundo) => {
  if (!corFundo) {
    return canvasOrigem;
  }
  const canvas = document.createElement('canvas');
  canvas.width = canvasOrigem.width;
  canvas.height = canvasOrigem.height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = corFundo;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(canvasOrigem, 0, 0);
  return canvas;
};

export const obterImagemAssinatura = ({
  dados = null,
  tipo = 'image/png',
  qualidade = 1,
  corFundo = null,
  corCaneta = '#000000',
  margem = 5,

  exportScale = 1, // Imagem de output será escalonada.
  // (Ex: exportScale = 2 e larguraMaxima = 200 ->
  // Imagem de output terá largura de 400px).

  espessuraMultiplicador = 1, // Imagem de output terá a largura/altura definida,
  // porém será feito upscale/downscale interno para mudar a espessura do traço.
  // (Ex: espessuraMultiplicador = 2 e larguraMaxima = 200 ->
  // Imagem de output terá largura de 200px, assinatura será
  // gerada num canvas de 400px e será redimensionada
  // pela metade, o que diminuirá a espessura do traço).

  larguraMaxima = null,
  alturaMaxima = null
} = {}) => {
  const canvas = criarCanvasAssinatura(dados, {
    corCaneta,
    margem,
    exportScale,
    espessuraMultiplicador,
    larguraMaxima,
    alturaMaxima
  });
  const canvasComFundo = aplicarCorFundoCanvas(canvas, corFundo);
  return canvasComFundo.toDataURL(tipo, qualidade);
};
